import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DeleteConfirmationDialog } from '@/components/DeleteConfirmationDialog';
import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import { usePostHog } from '@/config/analytics/posthog';
import {
  NarrativeCard,
  type NarrativeCardData,
  NarrativeCardSkeleton,
} from '@/features/narrative/components/NarrativeCard';
import { NarrativeEmptyState } from '@/features/narrative/components/NarrativeEmptyState';

interface NarrativeCardsListProps {
  filterType?: 'today' | 'week' | 'custom';
  isLoading: boolean;
  narratives: Array<NarrativeCardData>;
  onDelete?: (id: string) => void;
  onSave?: (id: string, content: string) => void;
}

export function NarrativeCardsList({
  filterType,
  isLoading,
  narratives,
  onDelete,
  onSave,
}: NarrativeCardsListProps) {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [narrativeToDelete, setNarrativeToDelete] = useState<string | null>(null);

  const handleDeleteClick = (id: string) => {
    setNarrativeToDelete(id);
  };

  const handleDeleteConfirm = async () => {
    if (!narrativeToDelete) {
      return;
    }
    if (onDelete) {
      onDelete(narrativeToDelete);
    }
    setNarrativeToDelete(null);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <NarrativeCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  // Show empty state when there are no narratives
  if (narratives.length === 0) {
    return (
      <>
        <NarrativeEmptyState {...(filterType && { filterType })} />
        {/* Delete Confirmation Modal */}
        <DeleteConfirmationDialog
          cancelLabel={t('narrative.cards.delete.cancel')}
          confirmLabel={t('narrative.cards.delete.confirm')}
          description={t('narrative.cards.delete.description')}
          onConfirm={handleDeleteConfirm}
          onOpenChange={(open) => {
            if (!open) {
              setNarrativeToDelete(null);
            }
          }}
          open={narrativeToDelete !== null}
          title={t('narrative.cards.delete.title')}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        {narratives.map((narrative) => (
          <NarrativeCard
            key={narrative.id}
            narrative={narrative}
            onCopySuccess={(id) => {
              posthog.capture(ANALYTICS_EVENTS.NARRATIVE_COPIED, {
                filterType: filterType ?? 'unknown',
                narrativeCopyCount: 1,
                narrativeId: id,
              });
            }}
            onDelete={handleDeleteClick}
            {...(onSave && { onSave })}
          />
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationDialog
        cancelLabel={t('narrative.cards.delete.cancel')}
        confirmLabel={t('narrative.cards.delete.confirm')}
        description={t('narrative.cards.delete.description')}
        onConfirm={handleDeleteConfirm}
        onOpenChange={(open) => {
          if (!open) {
            setNarrativeToDelete(null);
          }
        }}
        open={narrativeToDelete !== null}
        title={t('narrative.cards.delete.title')}
      />
    </>
  );
}
